import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Box,
  Avatar,
  Chip,
  Tooltip,
  Fade,
  Paper,
  Divider,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";

// Icons
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CachedIcon from "@material-ui/icons/Cached";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import PersonIcon from "@material-ui/icons/Person";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";

import logo from "../assets/logo.png";
import { SocketContext } from "../context/Socket/SocketContext";
import ChatPopover from "../pages/Chat/ChatPopover";

import { useDate } from "../hooks/useDate";

import ColorModeContext from "../layout/themeContext";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import LanguageControl from "../components/LanguageControl";
import { LanguageOutlined } from "@material-ui/icons";

const drawerWidth = 280;
const drawerWidthClosed = 72;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    backgroundColor: theme.palette.type === "dark" ? "#0a0a0f" : "#f0f4f8",
    overflow: "hidden",
  },

  // ========== SIDEBAR MODERNA ==========
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      width: 0,
    },
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeOut,
      duration: 300,
    }),
  },
  drawerClose: {
    width: drawerWidthClosed,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeOut,
      duration: 300,
    }),
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      width: 0,
    },
  },
  drawerPaper: {
    width: drawerWidth,
    border: "none",
    background: theme.palette.type === "dark" 
      ? "linear-gradient(180deg, #12121a 0%, #0d0d14 100%)"
      : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: theme.palette.type === "dark"
      ? "4px 0 24px rgba(0, 0, 0, 0.4)"
      : "4px 0 24px rgba(0, 0, 0, 0.06)",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeOut,
      duration: 300,
    }),
    overflowX: "hidden",
    ...theme.scrollbarStylesSoft,
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      zIndex: 1300,
    },
  },
  drawerPaperClose: {
    width: drawerWidthClosed,
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      width: 0,
    },
  },

  // Logo Header
  logoHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2),
    minHeight: 70,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  },
  logoWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  logoIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
  },
  logoImage: {
    width: 24,
    height: 24,
    filter: "brightness(0) invert(1)",
  },
  logoText: {
    fontSize: "1.1rem",
    fontWeight: 700,
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    letterSpacing: "-0.02em",
  },
  toggleButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      transform: "scale(1.05)",
    },
  },

  // ========== TOP BAR MODERNA ==========
  appBar: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    background: theme.palette.type === "dark"
      ? "rgba(18, 18, 26, 0.8)"
      : "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(20px)",
    boxShadow: "none",
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.easeOut,
      duration: 300,
    }),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "100%",
    },
  },
  appBarShift: {
    marginLeft: drawerWidthClosed,
    width: `calc(100% - ${drawerWidthClosed}px)`,
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "100%",
    },
  },
  toolbar: {
    minHeight: 70,
    padding: theme.spacing(0, 3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      minHeight: 56,
      padding: theme.spacing(0, 1.5),
    },
  },

  // Left Section
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1),
    },
  },
  welcomeBox: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  welcomeText: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.7rem",
    },
  },
  companyName: {
    fontSize: "1rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.85rem",
    },
  },

  // Search Bar
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: 12,
    backgroundColor: alpha(theme.palette.action.hover, 0.6),
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.action.hover, 1),
      borderColor: theme.palette.primary.main,
    },
  },
  searchText: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  searchShortcut: {
    fontSize: "0.7rem",
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 6,
    backgroundColor: alpha(theme.palette.divider, 0.4),
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },

  // Right Section
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    "& .MuiIconButton-root": {
      color: theme.palette.text.secondary,
      transition: "all 0.2s ease",
      "&:hover": {
        color: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
      },
    },
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(0.5),
    },
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "transparent",
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    color: theme.palette.text.secondary,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      transform: "translateY(-2px)",
    },
    [theme.breakpoints.down("sm")]: {
      width: 36,
      height: 36,
      borderRadius: 8,
      border: "none",
    },
  },

  // User Menu
  userButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 1.5, 0.75, 0.75),
    borderRadius: 12,
    backgroundColor: alpha(theme.palette.action.hover, 0.4),
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.action.hover, 0.8),
      borderColor: alpha(theme.palette.primary.main, 0.3),
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.5),
      gap: theme.spacing(0.5),
      borderRadius: 10,
    },
  },
  userAvatar: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    fontSize: "0.85rem",
    fontWeight: 700,
    [theme.breakpoints.down("sm")]: {
      width: 32,
      height: 32,
      borderRadius: 8,
      fontSize: "0.75rem",
    },
  },
  userName: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    maxWidth: 100,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userRole: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },

  // Menu
  menuPaper: {
    borderRadius: 16,
    marginTop: theme.spacing(1),
    minWidth: 220,
    boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.2)",
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    padding: theme.spacing(1),
  },
  menuItem: {
    borderRadius: 10,
    padding: theme.spacing(1.25, 2),
    margin: theme.spacing(0.25, 0),
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },
  menuItemIcon: {
    marginRight: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  menuDivider: {
    margin: theme.spacing(1, 0),
  },

  // Content
  content: {
    flexGrow: 1,
    height: "100vh",
    overflow: "auto",
    backgroundColor: theme.palette.type === "dark" ? "#0a0a0f" : "#f0f4f8",
    ...theme.scrollbarStyles,
  },
  contentSpacer: {
    minHeight: 70,
    [theme.breakpoints.down("sm")]: {
      minHeight: 56,
    },
  },
  // Mobile drawer overlay
  drawerBackdrop: {
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1299,
    },
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const { user } = useContext(AuthContext);

  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));
  const greaterThenMd = useMediaQuery(theme.breakpoints.up("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);
  const { dateToClient } = useDate();

  const [anchorElLanguage, setAnchorElLanguage] = useState(null);
  const [menuLanguageOpen, setMenuLanguageOpen] = useState(false);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    if (greaterThenMd) {
      setDrawerOpen(true);
    } else {
      setDrawerOpen(false);
    }
  }, [greaterThenMd]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");

    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-auth`, (data) => {
      if (data.user.id === +userId) {
        toastError("Sua conta foi acessada em outro computador.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    });

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, [socketManager]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handlemenuLanguage = (event) => {
    setAnchorElLanguage(event.currentTarget);
    setMenuLanguageOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleCloseMenuLanguage = () => {
    setAnchorElLanguage(null);
    setMenuLanguageOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (!greaterThenMd) {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      {/* Mobile Backdrop */}
      {isMobile && drawerOpen && (
        <Box 
          className={classes.drawerBackdrop} 
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* ========== SIDEBAR ========== */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: drawerOpen,
          [classes.drawerClose]: !drawerOpen,
        })}
        classes={{
          paper: clsx(classes.drawerPaper, {
            [classes.drawerOpen]: drawerOpen,
            [classes.drawerPaperClose]: !drawerOpen && !isMobile,
          }),
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {/* Logo Header */}
        <Box className={classes.logoHeader}>
          <Box className={classes.logoWrapper}>
            <Box className={classes.logoIcon}>
              <img src={logo} alt="Logo" className={classes.logoImage} />
            </Box>
            <Fade in={drawerOpen}>
              <Typography className={classes.logoText}>
                Omni
              </Typography>
            </Fade>
          </Box>
          <Fade in={drawerOpen}>
            <IconButton
              size="small"
              className={classes.toggleButton}
              onClick={() => setDrawerOpen(!drawerOpen)}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Fade>
        </Box>

        {/* Navigation */}
        <MainListItems drawerClose={drawerClose} collapsed={!drawerOpen} />

        {/* Toggle quando fechado */}
        {!drawerOpen && (
          <Box display="flex" justifyContent="center" py={2}>
            <IconButton
              size="small"
              className={classes.toggleButton}
              onClick={() => setDrawerOpen(true)}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Drawer>

      {/* User Modal */}
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      {/* ========== TOP BAR ========== */}
      <AppBar
        position="fixed"
        color="inherit"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: !drawerOpen,
        })}
        elevation={0}
      >
        <Toolbar className={classes.toolbar}>
          {/* Left */}
          <Box className={classes.toolbarLeft}>
            {(isMobile || !drawerOpen) && (
              <IconButton
                className={classes.toggleButton}
                onClick={() => setDrawerOpen(true)}
                style={{ marginRight: 8 }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            )}
            
            <Box className={classes.welcomeBox}>
              <Typography className={classes.welcomeText}>
                Bem-vindo de volta 👋
              </Typography>
              <Typography className={classes.companyName}>
                {user?.company?.name || "Omni"}
              </Typography>
            </Box>
          </Box>

          {/* Right */}
          <Box className={classes.toolbarRight}>
            {/* Language */}
            <Tooltip title="Idioma">
              <IconButton
                className={classes.actionButton}
                onClick={handlemenuLanguage}
              >
                <LanguageOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorElLanguage}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={menuLanguageOpen}
              onClose={handleCloseMenuLanguage}
              classes={{ paper: classes.menuPaper }}
            >
              <MenuItem className={classes.menuItem}>
                <LanguageControl />
              </MenuItem>
            </Menu>

            {/* Theme */}
            <Tooltip title={theme.palette.type === "dark" ? "Modo Claro" : "Modo Escuro"}>
              <IconButton className={classes.actionButton} onClick={toggleColorMode}>
                {theme.palette.type === "dark" ? (
                  <Brightness7Icon fontSize="small" />
                ) : (
                  <Brightness4Icon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            {/* Refresh */}
            <Tooltip title="Atualizar">
              <IconButton className={classes.actionButton} onClick={handleRefreshPage}>
                <CachedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Volume */}
            <NotificationsVolume setVolume={setVolume} volume={volume} />

            {/* Notifications */}
            {user.id && <NotificationsPopOver volume={volume} />}

            {/* Announcements */}
            <AnnouncementsPopover />

            {/* Chat */}
            <ChatPopover />

            {/* User Menu */}
            <Box className={classes.userButton} onClick={handleMenu}>
              <Avatar className={classes.userAvatar}>
                {getInitials(user.name)}
              </Avatar>
              {greaterThenSm && (
                <Box>
                  <Typography className={classes.userName}>
                    {user.name?.split(" ")[0]}
                  </Typography>
                  <Typography className={classes.userRole}>
                    {user.profile === "admin" ? "Administrador" : "Atendente"}
                  </Typography>
                </Box>
              )}
              <ExpandMoreIcon fontSize="small" style={{ opacity: 0.5 }} />
            </Box>

            <Menu
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={menuOpen}
              onClose={handleCloseMenu}
              classes={{ paper: classes.menuPaper }}
            >
              <Box px={2} py={1.5}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  {user.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {user.email}
                </Typography>
              </Box>
              <Divider className={classes.menuDivider} />
              <MenuItem onClick={handleOpenUserModal} className={classes.menuItem}>
                <PersonIcon fontSize="small" className={classes.menuItemIcon} />
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout} className={classes.menuItem}>
                <ExitToAppIcon fontSize="small" className={classes.menuItemIcon} />
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ========== CONTENT ========== */}
      <main className={classes.content}>
        <div className={classes.contentSpacer} />
        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
